import {
  CButton,
  CCol,
  CContainer,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CRow,
} from "@coreui/react";
import { useState } from "react";
import { Spinner } from "react-bootstrap";
import jsPDF from "jspdf";
import JsBarcode from "jsbarcode";
import InventoryInputBatch from "../../models/inventory-input-batch";
import { formatDateTime } from "../../utils/dates";

interface InventoryInputBatchBarcodePrintModalProps {
  show: boolean;
  items: InventoryInputBatch[];
  onCancel: () => void | Promise<void>;
  onSuccess: () => void | Promise<void>;
}

const InventoryInputBatchBarcodePrintModal: React.FC<
  InventoryInputBatchBarcodePrintModalProps
> = ({ show, items, onCancel, onSuccess }) => {
  const [submitting, setSubmitting] = useState(false);

  const generateAndPrintPDF = (batches: InventoryInputBatch[]) => {
    const customWidth = 56;
    const customHeight = 124;
    const margin = 5;
    const stickersPerPage = 4;
    const stickerHeight =
      (customHeight - (stickersPerPage + 1) * margin) / stickersPerPage;
    // stickerHeight = ~24.75mm

    const pdf = new jsPDF({
      orientation: "p",
      unit: "mm",
      format: [customWidth, customHeight],
    });

    const printableWidth = customWidth - 2 * margin; // 46mm

    const truncateText = (text: string, maxWidth: number): string => {
      let truncated = text;
      const ellipsis = "...";
      while (
        pdf.getTextWidth(truncated + ellipsis) > maxWidth &&
        truncated.length > 0
      ) {
        truncated = truncated.slice(0, -1);
      }
      return truncated + ellipsis;
    };

    const printCenteredText = (
      text: string,
      maxWidth: number,
      y: number,
      fontSize: number,
    ) => {
      pdf.setFontSize(fontSize);
      const textWidth = pdf.getTextWidth(text);
      const displayText =
        textWidth > maxWidth ? truncateText(text, maxWidth) : text;
      const finalWidth = pdf.getTextWidth(displayText);
      const x = margin + (maxWidth - finalWidth) / 2;
      pdf.text(displayText, x, y);
    };

    let currentStickerCount = 0;
    let y = margin;

    batches.forEach((batch) => {
      if (!batch.fabricBarcode) return;

      if (currentStickerCount === stickersPerPage) {
        pdf.addPage();
        currentStickerCount = 0;
        y = margin;
      }

      // --- Layout within stickerHeight (~24.75mm) ---
      // Line 1 — Material name            → y + 3
      // Line 2 — Provider                 → y + 6
      // Barcode (11mm tall)               → y + 8  to  y + 19
      // Line 3 — Date                     → y + 20.5
      // Line 4 — Cant. Total              → y + 22
      // Line 5 — Lote (optional)          → y + 23.5

      const barcodeTop = y + 8;
      const barcodeHeight = 11;

      // Line 1: Input material
      const materialText = (batch.inputMaterialIdentifier || "—").toUpperCase();
      printCenteredText(materialText, printableWidth, y + 3, 6);

      // Line 2: Provider
      if (batch.supplierName) {
        printCenteredText(
          `Prov: ${batch.supplierName}`,
          printableWidth,
          y + 6,
          5,
        );
      }

      // Barcode
      const canvas = document.createElement("canvas");
      JsBarcode(canvas, batch.fabricBarcode, {
        format: "EAN13",
        displayValue: true,
        fontSize: 20,
        marginTop: 0,
        marginBottom: 0,
        marginLeft: 30,
        marginRight: 30,
      });

      const imgData = canvas.toDataURL("image/png");
      pdf.addImage(
        imgData,
        "PNG",
        margin,
        barcodeTop,
        printableWidth,
        barcodeHeight,
      );

      // Line 3: Date only
      const dateText = batch.purchaseDate
        ? new Date(batch.purchaseDate).toLocaleDateString("es-PY", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })
        : "";
      if (dateText) {
        printCenteredText(dateText, printableWidth, y + 20.5, 5);
      }

      // Line 4: Cant. Total
      printCenteredText(
        `Cant. Total: ${batch.amount ?? "—"}`,
        printableWidth,
        y + 22,
        5,
      );

      // Line 5: Lote (only if present)
      if (batch.fabricCode) {
        printCenteredText(
          `Lote: ${batch.fabricCode}`,
          printableWidth,
          y + 23.5,
          5,
        );
      }

      // Cleanup canvas
      const ctx = canvas.getContext("2d");
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);

      y += stickerHeight + margin;
      currentStickerCount++;
    });

    pdf.autoPrint();
    const blob = pdf.output("blob");
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  const onConfirmClick = async () => {
    setSubmitting(true);
    generateAndPrintPDF(items);
    setSubmitting(false);
    onSuccess();
  };

  return (
    <CModal show={show} className="modal-primary" onClosed={onCancel} size="xl">
      <CModalHeader closeButton>
        <CModalTitle>Confirmar Impresión de Códigos de Barra</CModalTitle>
      </CModalHeader>
      <CModalBody>
        <CContainer fluid>
          <CRow>
            <CCol sm="12">
              <h2>
                Se imprimirán <strong>{items.length}</strong> código(s) de
                barra.
              </h2>
            </CCol>
          </CRow>
          <CRow className="mt-2">
            <CCol sm="12">
              <table className="table table-sm table-bordered">
                <thead>
                  <tr>
                    <th>Materia Prima</th>
                    <th>Proveedor</th>
                    <th>Fecha</th>
                    <th>Cant.</th>
                    <th>Código de Barra</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.inputMaterialIdentifier}</td>
                      <td>{item.supplierName || "—"}</td>
                      <td>
                        {item.purchaseDate
                          ? formatDateTime(new Date(item.purchaseDate))
                          : "—"}
                      </td>
                      <td>{item.amount}</td>
                      <td>
                        <code>{item.fabricBarcode}</code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CCol>
          </CRow>
          <CRow>
            <CCol sm="12">
              <h6 className="text-muted">
                Asegúrese de que la impresora de etiquetas esté conectada.
              </h6>
            </CCol>
          </CRow>
        </CContainer>
      </CModalBody>
      <CModalFooter>
        <CButton disabled={submitting} onClick={onCancel} color="secondary">
          Cancelar
        </CButton>
        <CButton disabled={submitting} onClick={onConfirmClick} color="primary">
          {submitting ? (
            <Spinner
              animation="grow"
              style={{ height: "17px", width: "17px", marginRight: "10px" }}
            />
          ) : (
            <></>
          )}
          {submitting ? "Generando..." : "Imprimir"}
        </CButton>
      </CModalFooter>
    </CModal>
  );
};

export default InventoryInputBatchBarcodePrintModal;
